import React, { useState } from 'react';
import './ChatComponents.css';
import AvatarMenu from './AvatarMenu';

const CBBTutorBubble = (props : any) => {
    const [menuPos, setMenuPos] = useState<{x: number; y: number} | null>(null);
    
    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        setMenuPos({ x: e.clientX + 10, y: e.clientY - 10 });
    };
    // Parse basic markdown and render as React elements
    const renderMarkdown = (text: string): React.ReactNode => {
        // Check if text contains braille/ASCII art characters
        const hasBraille = /[\u2800-\u28FF]/.test(text);
        if (hasBraille) {
            const parts = text.split(/([\u2800-\u28FF\s]+\n?)+/g);
            const result: React.ReactNode[] = [];
            let inArt = false;
            let artBuffer = '';
            
            for (const line of text.split('\n')) {
                if (/[\u2800-\u28FF]/.test(line)) {
                    artBuffer += line + '\n';
                    inArt = true;
                } else {
                    if (inArt && artBuffer) {
                        result.push(<pre key={result.length} style={{ whiteSpace: 'pre', fontFamily: 'monospace', fontSize: '12px', lineHeight: '1.1', margin: '8px 0' }}>{artBuffer}</pre>);
                        artBuffer = '';
                        inArt = false;
                    }
                    if (line.trim()) {
                        result.push(<div key={result.length} className="markdown-paragraph">{parseInlineMarkdown(line)}</div>);
                    }
                }
            }
            if (artBuffer) {
                result.push(<pre key={result.length} style={{ whiteSpace: 'pre', fontFamily: 'monospace', fontSize: '12px', lineHeight: '1.1', margin: '8px 0' }}>{artBuffer}</pre>);
            }
            return result;
        }
        
        // Split into lines first
        const lines = text.split('\n');
        
        // Detect and render markdown tables
        const result: React.ReactNode[] = [];
        let i = 0;
        
        while (i < lines.length) {
            // Check if this line starts a table (has pipes)
            if (lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
                const tableLines: string[] = [];
                while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
                    tableLines.push(lines[i]);
                    i++;
                }
                
                if (tableLines.length >= 2) {
                    // Parse header row
                    const headerCells = tableLines[0].split('|').filter(c => c.trim() !== '').map(c => c.trim());
                    // Skip separator row (index 1), parse data rows
                    const dataRows = tableLines.slice(2).map(row => 
                        row.split('|').filter(c => c.trim() !== '').map(c => c.trim())
                    );
                    
                    result.push(
                        <table key={`table-${i}`} className="markdown-table">
                            <thead>
                                <tr>
                                    {headerCells.map((cell, ci) => (
                                        <th key={ci}>{parseInlineMarkdown(cell)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {dataRows.map((row, ri) => (
                                    <tr key={ri}>
                                        {row.map((cell, ci) => (
                                            <td key={ci}>{parseInlineMarkdown(cell)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    );
                    continue;
                }
            }
            
            const line = lines[i];
            i++;
            // Check for headers (### Header, ## Header, # Header)
            const h3Match = line.match(/^###\s+(.*)$/);
            const h2Match = line.match(/^##\s+(.*)$/);
            const h1Match = line.match(/^#\s+(.*)$/);
            
            if (h3Match) {
                result.push(
                    <div key={`line-${i}`} className="markdown-h3">
                        {parseInlineMarkdown(h3Match[1])}
                    </div>
                );
                continue;
            }
            
            if (h2Match) {
                result.push(
                    <div key={`line-${i}`} className="markdown-h2">
                        {parseInlineMarkdown(h2Match[1])}
                    </div>
                );
                continue;
            }
            
            if (h1Match) {
                result.push(
                    <div key={`line-${i}`} className="markdown-h1">
                        {parseInlineMarkdown(h1Match[1])}
                    </div>
                );
                continue;
            }
            
            // Check if line is a list item
            const bulletMatch = line.match(/^(\s*)[-*•]\s+(.*)$/);
            const numberedMatch = line.match(/^(\s*)(\d+)\.\s+(.*)$/);
            
            if (bulletMatch) {
                const indent = bulletMatch[1].length;
                const content = bulletMatch[2];
                result.push(
                    <div key={`line-${i}`} className="markdown-list-item" style={{ marginLeft: `${indent * 8 + 16}px` }}>
                        <span className="markdown-bullet">•</span>
                        <span>{parseInlineMarkdown(content)}</span>
                    </div>
                );
                continue;
            }
            
            if (numberedMatch) {
                const indent = numberedMatch[1].length;
                const number = numberedMatch[2];
                const content = numberedMatch[3];
                result.push(
                    <div key={`line-${i}`} className="markdown-list-item" style={{ marginLeft: `${indent * 8 + 16}px` }}>
                        <span className="markdown-number">{number}.</span>
                        <span>{parseInlineMarkdown(content)}</span>
                    </div>
                );
                continue;
            }
            
            // Empty line - add spacing
            if (line.trim() === '') {
                result.push(<div key={`line-${i}`} className="markdown-spacer" />);
                continue;
            }
            
            // Regular line
            result.push(
                <div key={`line-${i}`} className="markdown-paragraph">
                    {parseInlineMarkdown(line)}
                </div>
            );
        }
        
        return result;
    };
    
    // Parse inline markdown (bold, italic, code, emoji)
    const parseInlineMarkdown = (text: string): React.ReactNode => {
        if (!text) return null;
        
        const parts: React.ReactNode[] = [];
        let remaining = text;
        let keyIndex = 0;
        
        while (remaining.length > 0) {
            // Bold: **text** or __text__
            const boldMatch = remaining.match(/^(.*?)\*\*(.+?)\*\*/);
            const boldMatch2 = remaining.match(/^(.*?)__(.+?)__/);
            
            // Inline code: `code`
            const codeMatch = remaining.match(/^(.*?)`(.+?)`/);
            
            // Find the earliest match
            let earliestMatch: { type: string; match: RegExpMatchArray } | null = null;
            let earliestIndex = Infinity;
            
            if (boldMatch && boldMatch.index !== undefined && boldMatch[1].length < earliestIndex) {
                earliestIndex = boldMatch[1].length;
                earliestMatch = { type: 'bold', match: boldMatch };
            }
            if (boldMatch2 && boldMatch2.index !== undefined && boldMatch2[1].length < earliestIndex) {
                earliestIndex = boldMatch2[1].length;
                earliestMatch = { type: 'bold', match: boldMatch2 };
            }
            if (codeMatch && codeMatch.index !== undefined && codeMatch[1].length < earliestIndex) {
                earliestIndex = codeMatch[1].length;
                earliestMatch = { type: 'code', match: codeMatch };
            }
            
            if (earliestMatch) {
                const { type, match } = earliestMatch;
                
                // Add text before the match
                if (match[1]) {
                    parts.push(<span key={keyIndex++}>{match[1]}</span>);
                }
                
                // Add the formatted element
                if (type === 'bold') {
                    parts.push(<strong key={keyIndex++} className="markdown-bold">{match[2]}</strong>);
                } else if (type === 'code') {
                    parts.push(<code key={keyIndex++} className="markdown-inline-code">{match[2]}</code>);
                }
                
                // Continue with the rest
                remaining = remaining.slice(match[0].length);
            } else {
                // No more matches, add remaining text
                parts.push(<span key={keyIndex++}>{remaining}</span>);
                break;
            }
        }
        
        return parts.length > 0 ? parts : text;
    };
    
    return (
        <div style={{ display: 'flex', width: '100%', gap: '16px', textAlign: 'left' }}>
            <div
                className="message-avatar message-avatar-assistant"
                title="QuickLab"
                onContextMenu={handleContextMenu}
                style={{ cursor: 'context-menu' }}
            >
                <img src="/cat.png" alt="QL" style={{ width: 24, height: 24, objectFit: 'contain' }} />
            </div>
            <div className="message-content markdown-content" style={{ textAlign: 'left' }}>
                {renderMarkdown(props.message.content)}
            </div>
            {menuPos && <AvatarMenu x={menuPos.x} y={menuPos.y} onClose={() => setMenuPos(null)} />}
        </div>
    )
}

export default CBBTutorBubble;